// TUT-450 — o portal externo: visão, drive, solicitações, presença e envio de relatório.
import {
  demoRegistrarSolicitacaoDoPortal,
  demoPortalConfirmarPresenca,
  demoPortalConfirmarRecebimentoDoProtocolo,
  demoPortalDrive,
  demoPortalDriveAccessLink,
  demoPortalEnviarRelatorio,
  demoPortalScopeAtual,
  demoPortalSolicitacoes,
  demoUploadPortalDrive,
} from "../portal-demo";
import {
  demoAceitarConvocacao,
  demoConfirmarPresencaNaConvocacao,
  demoConvocacoes,
  demoEnviarEntrega,
  demoRecusarConvocacao,
  demoRegistrarResultadoPeloPortal,
} from "../demo/portal-representante";
import { demoVisaoGeralDoPortal } from "../demo/visao-geral-do-portal";
import { enviarAoArmazenamento, type RelatorDeProgresso } from "../upload-transport";
import type {
  ConvocacaoDoRepresentante,
  ExpertReport,
  CreatePortalServiceRequestInput,
  PortalDriveDocument,
  PortalDriveQuery,
  PortalDriveResponse,
  PortalDriveUploadInput,
  PortalScope,
  PortalServiceRequestItem,
  PortalServiceRequestListResponse,
  ResultadoDaPericiaInput,
  ResultadoDaPericiaResponse,
} from "../types";
import { ApiError } from "../api-error";
import { espelharSolicitacaoNoEscritorio } from "./ponte-do-portal";
import { request, sha256 } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

/**
 * TUT-531 — a solicitação no modo demonstração: registra no portal e, pela
 * ponte, ela chega à fila do escritório com a ficha e os anexos (como no
 * servidor, onde é o mesmo banco). A recusa do portal sai antes de a ponte rodar.
 */
async function registrarNoDemo(input: CreatePortalServiceRequestInput): Promise<PortalServiceRequestItem> {
  const { item, anexos } = demoRegistrarSolicitacaoDoPortal(input);
  await espelharSolicitacaoNoEscritorio(input, item, anexos);
  return item;
}

/**
 * TUT-507 — uma entrega do representante (o Relatório Técnico ou a NF/Recibo):
 * prepara no servidor (que confere a convocação, a conta e o arquivo), envia ao
 * armazenamento e conclui (hash, antivírus e o vínculo com a conta).
 */
async function enviarEntregaDoRepresentante(
  expertiseId: string,
  caminho: "relatorio-tecnico" | "nf-recibo",
  file: File,
  aoProgredir?: RelatorDeProgresso,
): Promise<ConvocacaoDoRepresentante> {
  aoProgredir?.({ fase: "preparando" });
  const base = `/portal/convocacoes/${encodeURIComponent(expertiseId)}/${caminho}/uploads`;
  const prepared = await request<{
    uploadId: string;
    upload: { url: string; method: "PUT"; headers: Record<string, string> };
  }>(base, {
    method: "POST",
    body: JSON.stringify({
      originalName: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      checksumSha256: await sha256(file),
    }),
  });
  const resposta = await enviarAoArmazenamento({
    url: prepared.upload.url,
    method: prepared.upload.method,
    headers: prepared.upload.headers,
    corpo: file,
    aoProgredir,
  });
  if (!resposta.ok) {
    throw new ApiError("O armazenamento recusou o arquivo. Tente novamente.", resposta.status);
  }
  aoProgredir?.({ fase: "concluindo" });
  return request<ConvocacaoDoRepresentante>(`${base}/${encodeURIComponent(prepared.uploadId)}/complete`, {
    method: "POST",
  });
}

/**
 * TUT-528 (D13) — o anexo do "Relatório finalizado" pelo Portal do assistente:
 * a mesma trilha do app (prepara, envia ao armazenamento, conclui), pela porta
 * do portal, que só abre na convocação aceita do próprio assistente externo.
 */
async function enviarRelatorioDoResultado(
  expertiseId: string,
  file: File,
  aoProgredir?: RelatorDeProgresso,
): Promise<{ id: string }> {
  aoProgredir?.({ fase: "preparando" });
  const base = `/portal/pericias/${encodeURIComponent(expertiseId)}/resultado/relatorio/uploads`;
  const prepared = await request<{
    uploadId: string;
    upload: { url: string; method: "PUT"; headers: Record<string, string> };
  }>(base, {
    method: "POST",
    body: JSON.stringify({
      originalName: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      checksumSha256: await sha256(file),
    }),
  });
  const resposta = await enviarAoArmazenamento({
    url: prepared.upload.url,
    method: prepared.upload.method,
    headers: prepared.upload.headers,
    corpo: file,
    aoProgredir,
  });
  if (!resposta.ok) {
    throw new ApiError("O armazenamento recusou o arquivo. Tente novamente.", resposta.status);
  }
  aoProgredir?.({ fase: "concluindo" });
  return request<{ id: string }>(`${base}/${encodeURIComponent(prepared.uploadId)}/complete`, {
    method: "POST",
  });
}

export const apiDoPortal = {
  /**
   * TUT-528 (D8, D13) — o pop-up do resultado da perícia no Portal do
   * assistente: o MESMO contrato de `POST /expertises/:id/resultado`. O anexo
   * do "Relatório finalizado" sobe antes, pela porta do portal, e o documento
   * viaja em `relatorioDocumentId`.
   */
  async portalRegistrarResultado(
    expertiseId: string,
    input: ResultadoDaPericiaInput,
    relatorio: File | null,
    aoProgredir?: RelatorDeProgresso,
  ): Promise<ResultadoDaPericiaResponse> {
    if (useDemoApi) {
      aoProgredir?.({ fase: "preparando" });
      const resultado = demoRegistrarResultadoPeloPortal(expertiseId, input, relatorio !== null);
      aoProgredir?.({ fase: "concluindo" });
      return resultado;
    }
    const documento = relatorio ? await enviarRelatorioDoResultado(expertiseId, relatorio, aoProgredir) : null;
    return request<ResultadoDaPericiaResponse>(`/portal/pericias/${encodeURIComponent(expertiseId)}/resultado`, {
      method: "POST",
      body: JSON.stringify(documento ? { ...input, relatorioDocumentId: documento.id } : input),
    });
  },
  /**
   * TUT-080 fase 2c — o alcance da pessoa externa logada, numa chamada só.
   * A rota é `@Portal()` no servidor: sessão interna recebe 403 aqui, e é o
   * RolesGuard quem garante — a tela não refiltra nada.
   */
  portalOverview(): Promise<PortalScope> {
    return useDemoApi
      ? Promise.resolve(structuredClone(demoVisaoGeralDoPortal(demoPortalScopeAtual())))
      : request<PortalScope>("/portal/overview");
  },
  portalDrive(query: PortalDriveQuery = {}): Promise<PortalDriveResponse> {
    if (useDemoApi) return Promise.resolve(demoPortalDrive(query));
    const params = new URLSearchParams();
    if (query.search) params.set("search", query.search);
    if (query.status) params.set("status", query.status);
    const suffix = params.size ? `?${params}` : "";
    return request<PortalDriveResponse>(`/portal/drive${suffix}`);
  },
  async uploadPortalDrive(
    input: PortalDriveUploadInput,
    aoProgredir?: RelatorDeProgresso,
  ): Promise<PortalDriveDocument> {
    if (useDemoApi) {
      // No modo demonstração não existe transferência: relatar "enviando" com
      // um número inventado seria exatamente a barra falsa que a fatia recusa.
      aoProgredir?.({ fase: "preparando" });
      const documento = await demoUploadPortalDrive(input);
      aoProgredir?.({ fase: "concluindo" });
      return documento;
    }
    aoProgredir?.({ fase: "preparando" });
    const checksumSha256 = await sha256(input.file);
    const prepared = await request<{
      uploadId: string;
      upload: {
        url: string;
        method: "PUT";
        headers: Record<string, string>;
      };
    }>("/portal/drive/uploads", {
      method: "POST",
      body: JSON.stringify({
        title: input.title,
        originalName: input.file.name,
        mimeType: input.file.type,
        sizeBytes: input.file.size,
        checksumSha256,
      }),
    });
    const uploadResponse = await enviarAoArmazenamento({
      url: prepared.upload.url,
      method: prepared.upload.method,
      headers: prepared.upload.headers,
      corpo: input.file,
      aoProgredir,
    });
    if (!uploadResponse.ok) {
      throw new ApiError(
        "O armazenamento recusou o arquivo. Tente novamente.",
        uploadResponse.status,
      );
    }
    aoProgredir?.({ fase: "concluindo" });
    return request<PortalDriveDocument>(
      `/portal/drive/uploads/${encodeURIComponent(prepared.uploadId)}/complete`,
      { method: "POST" },
    );
  },
  portalDriveAccessLink(
    documentId: string,
    versionId: string,
    mode: "download" | "inline",
  ): Promise<{ url: string; expiresInSeconds: number }> {
    if (useDemoApi) return Promise.resolve(demoPortalDriveAccessLink());
    const suffix = mode === "inline" ? "?modo=inline" : "";
    return request<{ url: string; expiresInSeconds: number }>(
      `/portal/drive/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/download${suffix}`,
    );
  },
  /**
   * TUT-294a — as solicitações do escritório-login (ou das dele, quando o
   * vínculo é do escritório). Rota `@Portal()`: perito recebe 403, e é o
   * servidor quem decide — a tela não refiltra.
   */
  portalSolicitacoes(): Promise<PortalServiceRequestListResponse> {
    return useDemoApi
      ? Promise.resolve(demoPortalSolicitacoes())
      : request<PortalServiceRequestListResponse>("/portal/solicitacoes");
  },
  portalCreateSolicitacao(
    input: CreatePortalServiceRequestInput,
  ): Promise<PortalServiceRequestItem> {
    if (useDemoApi) return registrarNoDemo(input);
    return request<PortalServiceRequestItem>("/portal/solicitacoes", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  /** TUT-295 — "confirmação de presença": só a partir de agendada, idempotente. */
  portalConfirmarPresenca(
    expertiseId: string,
  ): Promise<{ id: string; presenceConfirmedAt: string | null }> {
    if (useDemoApi) return Promise.resolve(demoPortalConfirmarPresenca(expertiseId));
    return request(`/portal/pericias/${encodeURIComponent(expertiseId)}/presenca`, {
      method: "POST",
    });
  },
  /** TUT-490d2 — a advogada aceita o protocolo do laudo (o retorno do R01); idempotente. */
  portalConfirmarRecebimentoDoProtocolo(
    reportId: string,
  ): Promise<{ id: string; protocolReceivedAt: string }> {
    if (useDemoApi) return Promise.resolve(demoPortalConfirmarRecebimentoDoProtocolo(reportId));
    return request(`/portal/laudos/${encodeURIComponent(reportId)}/recebimento`, {
      method: "POST",
    });
  },
  /** TUT-507 — as convocações do representante local (só as dele, com a conta dele). */
  portalConvocacoes(): Promise<{ items: ConvocacaoDoRepresentante[] }> {
    if (useDemoApi) return Promise.resolve(demoConvocacoes());
    return request("/portal/convocacoes");
  },
  portalAceitarConvocacao(expertiseId: string): Promise<ConvocacaoDoRepresentante> {
    if (useDemoApi) return Promise.resolve(demoAceitarConvocacao(expertiseId));
    return request(`/portal/convocacoes/${encodeURIComponent(expertiseId)}/aceite`, { method: "POST" });
  },
  portalRecusarConvocacao(expertiseId: string, motivo: string): Promise<ConvocacaoDoRepresentante> {
    if (useDemoApi) return Promise.resolve(demoRecusarConvocacao(expertiseId, motivo));
    return request(`/portal/convocacoes/${encodeURIComponent(expertiseId)}/recusa`, {
      method: "POST",
      body: JSON.stringify({ motivo }),
    });
  },
  portalConfirmarPresencaNaConvocacao(expertiseId: string): Promise<ConvocacaoDoRepresentante> {
    if (useDemoApi) return Promise.resolve(demoConfirmarPresencaNaConvocacao(expertiseId));
    return request(`/portal/convocacoes/${encodeURIComponent(expertiseId)}/presenca`, { method: "POST" });
  },
  /** TUT-507 — o Relatório Técnico ou a NF/Recibo; as duas liberam a conta sozinhas. */
  async portalEnviarEntrega(
    expertiseId: string,
    entrega: "relatorio" | "nf",
    file: File,
    aoProgredir?: RelatorDeProgresso,
  ): Promise<ConvocacaoDoRepresentante> {
    if (useDemoApi) {
      // Sem transferência no demo: nada de barra de progresso inventada.
      aoProgredir?.({ fase: "preparando" });
      const resultado = demoEnviarEntrega(expertiseId, entrega);
      aoProgredir?.({ fase: "concluindo" });
      return resultado;
    }
    return enviarEntregaDoRepresentante(expertiseId, entrega === "relatorio" ? "relatorio-tecnico" : "nf-recibo", file, aoProgredir);
  },
  /** TUT-295 — "envio do relatório": o documento já subiu pelo drive pessoal. */
  portalEnviarRelatorio(
    expertiseId: string,
    documentId: string,
  ): Promise<ExpertReport> {
    if (useDemoApi) return Promise.resolve(demoPortalEnviarRelatorio(expertiseId, documentId));
    return request(`/portal/pericias/${encodeURIComponent(expertiseId)}/relatorio`, {
      method: "POST",
      body: JSON.stringify({ documentId }),
    });
  },
};
